import React from "react";
import { COLORS, TYPOGRAPHY, SPACING, SHADOWS, RADIUS, TRANSITIONS, GRADIENTS } from '../constants/designTokens';
import UnifiedCard from './UnifiedCard';

const helpItems = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" width="40" height="40">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        <path d="M8 10h8v2H8zm0 3h8v2H8z" opacity="0.6"/>
      </svg>
    ),
    title: "Free Expert Consultation",
    description: "We provide complimentary consultations to identify the exact spare parts your vessels need, whether you operate private yachts or commercial fleets."
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" width="40" height="40">
        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
        <circle cx="12" cy="4" r="1" opacity="0.6"/>
        <path d="M4 2h16v1H4zm0 19h16v1H4z" opacity="0.4"/>
      </svg>
    ),
    title: "Tailored Solutions for Every Vessel",
    description: "We customize our sourcing and supply packages to match your operational needs, vessel types, equipment brands, and budget requirements. From single components to full inventory restocking."
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" width="40" height="40">
        <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/>
        <path d="M7 7h2v2H7zm0 4h2v2H7zm10-4h2v2h-2zm0 8h2v2h-2z" opacity="0.7"/>
        <circle cx="5" cy="5" r="1" opacity="0.5"/>
        <circle cx="19" cy="5" r="1" opacity="0.5"/>
        <circle cx="5" cy="19" r="1" opacity="0.5"/>
        <circle cx="19" cy="19" r="1" opacity="0.5"/>
      </svg>
    ),
    title: "Fast Global Procurement & Delivery",
    description: "Beyond sourcing, we manage logistics and documentation to ensure the right parts reach your vessel or port of call quickly, minimizing downtime and keeping operations on schedule."
  }
];

const HowWeHelp = () => (
  <section id="how-we-help" className="howwehelp-section py-5" style={{ background: COLORS.background.white, position: "relative" }}>
    <style>{`
      .howwehelp-section h2 {
        color: ${COLORS.primary.dark};
        font-weight: ${TYPOGRAPHY.fontWeight.bold};
        margin-bottom: ${SPACING['3xl']};
        text-align: center;
        position: relative;
      }
      .howwehelp-section h2::after {
        content: '';
        position: absolute;
        bottom: -8px;
        left: 50%;
        transform: translateX(-50%);
        width: 60px;
        height: 3px;
        background: ${GRADIENTS.primary};
        border-radius: ${RADIUS.sm};
      }
      .help-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
        gap: ${SPACING['2xl']};
        max-width: 1100px;
        margin: 0 auto;
      }
      .help-item {
        background: linear-gradient(135deg, ${COLORS.background.light} 0%, ${COLORS.background.white} 100%);
        border: 2px solid transparent;
        border-radius: ${RADIUS.xl};
        padding: ${SPACING['2xl']} ${SPACING.xl};
        text-align: center;
        transition: ${TRANSITIONS.all};
        position: relative;
        overflow: hidden;
      }
      .help-item::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        right: 100%;
        height: 4px;
        background: ${GRADIENTS.teal};
        transition: all 0.4s ease;
      }
      .help-item:hover {
        transform: translateY(-8px);
        box-shadow: ${SHADOWS['2xl']};
        border-color: rgba(64, 168, 196, 0.3);
        background: linear-gradient(135deg, ${COLORS.background.white} 0%, ${COLORS.background.light} 100%);
      }
      .help-item:hover::before {
        left: 0;
        right: 0;
      }
      .help-icon {
        background: ${GRADIENTS.teal};
        border-radius: ${RADIUS.full};
        width: 80px;
        height: 80px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: ${COLORS.text.white};
        font-size: 2.2rem;
        margin: 0 auto ${SPACING.lg};
        box-shadow: 0 6px 25px rgba(64, 168, 196, 0.3);
        transition: ${TRANSITIONS.all};
        position: relative;
      }
      .help-icon::after {
        content: '';
        position: absolute;
        top: -5px;
        left: -5px;
        right: -5px;
        bottom: -5px;
        background: linear-gradient(135deg, rgba(64, 168, 196, 0.2) 0%, transparent 100%);
        border-radius: ${RADIUS.full};
        z-index: -1;
        opacity: 0;
        transition: opacity 0.3s ease;
      }
      .help-item:hover .help-icon {
        transform: scale(1.1) rotate(10deg);
        box-shadow: 0 8px 30px rgba(64, 168, 196, 0.4);
      }
      .help-item:hover .help-icon::after {
        opacity: 1;
      }
      .help-title {
        font-size: ${TYPOGRAPHY.fontSize.lg};
        font-weight: ${TYPOGRAPHY.fontWeight.bold};
        color: ${COLORS.primary.dark};
        margin-bottom: ${SPACING.md};
        line-height: ${TYPOGRAPHY.lineHeight.tight};
      }
      .help-description {
        color: ${COLORS.text.primary};
        font-size: ${TYPOGRAPHY.fontSize.base};
        line-height: ${TYPOGRAPHY.lineHeight.relaxed};
        margin: 0;
      }
      
      @media (max-width: 991px) {
        .help-grid {
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
        }
      }

      @media (max-width: 768px) {
        .help-grid {
          grid-template-columns: 1fr;
          gap: ${SPACING.xl};
        }
        .help-item {
          padding: ${SPACING.xl} ${SPACING.lg};
        }
        .help-icon {
          width: 70px;
          height: 70px;
          font-size: 2rem;
        }
        .help-title {
          font-size: ${TYPOGRAPHY.fontSize.md};
        }
        .help-description {
          font-size: ${TYPOGRAPHY.fontSize.sm};
        }
      }

      @media (max-width: 576px) {
        .help-grid {
          gap: ${SPACING.lg};
        }
        .help-item {
          padding: ${SPACING.lg} ${SPACING.md};
        }
        .help-icon {
          width: 65px;
          height: 65px;
        }
      }

      @media (max-width: 480px) {
        .help-item {
          padding: ${SPACING.lg} ${SPACING.md};
        }
        .help-title {
          font-size: ${TYPOGRAPHY.fontSize.md};
        }
        .help-description {
          font-size: ${TYPOGRAPHY.fontSize.sm};
        }
      }
    `}</style>
    
    <div className="container">
      <h2>How Can We Help Your Fleet?</h2>
      <div className="help-grid">
        {helpItems.map((item, idx) => (
          <UnifiedCard key={idx} variant="default">
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: '16px'
            }}>
              <div className="help-icon" style={{
                background: GRADIENTS.teal,
                borderRadius: '50%',
                width: '80px',
                height: '80px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: COLORS.text.white,
                fontSize: '2.2rem',
                boxShadow: '0 6px 25px rgba(64, 168, 196, 0.3)',
              }}>
                {item.icon}
              </div>
              <h3 className="help-title" style={{
                fontSize: TYPOGRAPHY.fontSize.lg,
                fontWeight: TYPOGRAPHY.fontWeight.bold,
                color: COLORS.primary.dark,
                lineHeight: TYPOGRAPHY.lineHeight.tight,
                margin: 0,
              }}>
                {item.title}
              </h3>
              <p className="help-description" style={{
                color: COLORS.text.primary,
                fontSize: TYPOGRAPHY.fontSize.base,
                lineHeight: TYPOGRAPHY.lineHeight.relaxed,
                margin: 0,
              }}>
                {item.description}
              </p>
            </div>
          </UnifiedCard>
        ))}
      </div>
    </div>
  </section>
);

export default HowWeHelp;
